import { TokenManager } from './TokenManager';
import { createLogger } from '../utils/logger';
import { createAuthDomain } from './api/authDomain';
import { createBillingDomain } from './api/billingDomain';
import { createMarketDomain } from './api/marketDomain';
import { createStatsDomain } from './api/statsDomain';
import type {
  ApiError,
  BillingHistoryItem,
  CheckoutStatusResponse,
  DetailedAnalytics,
  HourlyStatsResponse,
  PlansResponse,
  SearchContentParams,
  SearchContentResponse,
  SentimentAnalysisResult,
  PriceLinkageResponse,
  PriceLinkageSessionType,
  TopPriceLinkageResponse,
  SpikesByTickerResponse,
  SpikesTopResponse,
  StripeCheckoutResponse,
  StripePortalResponse,
  TickerContribution,
  TopTickersResponse,
  UserKarma,
  VerifyResponse,
} from './api/types';

export * from './api/types';

const logger = createLogger('ApiService');
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-v2.sentihype.com';

class ApiService {
  private tokenManager: TokenManager;
  private readonly authDomain: ReturnType<typeof createAuthDomain>;
  private readonly billingDomain: ReturnType<typeof createBillingDomain>;
  private readonly marketDomain: ReturnType<typeof createMarketDomain>;
  private readonly statsDomain: ReturnType<typeof createStatsDomain>;

  constructor() {
    this.tokenManager = new TokenManager(
      () => {
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        if (accessToken) {
          return {
            accessToken,
            refreshToken: refreshToken || '',
          };
        }
        return null;
      },
      (tokens) => {
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
      },
      () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('token');
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('idToken');
        localStorage.removeItem('email');
        localStorage.removeItem('subscriptionPlan');
      },
      async (refreshToken: string) => {
        const trimmed = refreshToken?.trim?.() ?? '';
        if (!trimmed) {
          const err = new Error('Refresh token missing') as ApiError;
          err.status = 400;
          throw err;
        }

        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          // camelCase (спека) + snake_case (если бэкенд на Jackson SNAKE_CASE)
          body: JSON.stringify({ refreshToken: trimmed, refresh_token: trimmed }),
        });

        if (!response.ok) {
          let detail = '';
          try {
            const body = await response.clone().json();
            detail = typeof body?.error === 'string' ? body.error : typeof body?.message === 'string' ? body.message : '';
          } catch {
            /* ignore */
          }
          const error = new Error(
            response.status === 429
              ? 'Too many requests. Please try again later.'
              : response.status === 401
                ? 'Refresh token expired. Please login again.'
                : response.status === 400
                  ? detail || 'Refresh rejected. Please log in again.'
                  : `Refresh failed: ${response.statusText}`
          ) as ApiError;
          error.status = response.status;
          throw error;
        }

        const data = await response.json();
        const responseAny = data as Record<string, unknown>;
        if (responseAny.token) {
          localStorage.setItem('token', String(responseAny.token));
        }
        if (responseAny.jwt_token) {
          localStorage.setItem('jwt_token', String(responseAny.jwt_token));
        }
        if (responseAny.idToken) {
          localStorage.setItem('idToken', String(responseAny.idToken));
        }

        return {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken || refreshToken,
        };
      },
      120
    );

    const deps = {
      apiBaseUrl: API_BASE_URL,
      request: this.request.bind(this),
      downgradeSessionToFree: this.downgradeSessionToFree.bind(this),
      tokenManager: this.tokenManager,
      logger,
    };

    this.authDomain = createAuthDomain(deps);
    this.billingDomain = createBillingDomain(deps);
    this.marketDomain = createMarketDomain(deps);
    this.statsDomain = createStatsDomain(deps);
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api-v2.sentihype.com';
    const url = `${baseUrl}${endpoint}`;

    const authHeader = await this.tokenManager.getAuthHeader();
    const headers = {
      'Content-Type': 'application/json',
      ...authHeader,
      ...options?.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.tokenManager.handle401AndRetry(async () => {
            const newAuthHeader = await this.tokenManager.getAuthHeader();
            const retryHeaders = {
              'Content-Type': 'application/json',
              ...newAuthHeader,
              ...options?.headers,
            };
            const retryResponse = await fetch(url, {
              ...options,
              headers: retryHeaders,
            });
            if (!retryResponse.ok) {
              if (retryResponse.status === 401) {
                throw new Error('Authentication failed. Please login again.');
              }
              throw new Error(`API request failed: ${retryResponse.statusText}`);
            }
            return retryResponse.json();
          });
        }

        if (response.status === 404) {
          if (endpoint.includes('/ticker/') || endpoint.includes('ticker=')) {
            throw new Error('Ticker not found');
          }
          throw new Error('Endpoint not found');
        }
        if (response.status === 403) {
          logger.warn(`Got 403 Forbidden for ${endpoint}`);
          const err = new Error('Session interrupted or no Premium access.') as ApiError;
          err.status = 403;
          err.is403 = true;
          throw err;
        }
        if (response.status === 429) {
          throw new Error('Too many requests. Please try again later.');
        }
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        throw new Error(`API request failed: ${response.statusText}`);
      }

      return response.json();
    } catch (error: unknown) {
      const apiError = error as ApiError;
      if (apiError.message === 'Failed to fetch' || apiError.name === 'TypeError' || apiError.message?.includes('CORS')) {
        logger.error(`Network/CORS error for ${endpoint}`, error);
        if (endpoint.includes('/pro/')) {
          const corsError = new Error('CORS error accessing Premium endpoint. You may need to use FREE endpoint or check your subscription.') as ApiError;
          corsError.isNetworkError = true;
          corsError.isCorsError = true;
          throw corsError;
        }
        const corsError = new Error('Network error or CORS issue. Please check your connection and backend CORS settings.') as ApiError;
        corsError.isNetworkError = true;
        corsError.isCorsError = true;
        throw corsError;
      }
      throw error;
    }
  }

  private downgradeSessionToFree(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('session-downgraded', { detail: { reason: '403' } }));
    }
    logger.warn('Session downgraded to FREE after 403');
  }

  async getTopTickersFree(
    hours: number = 6,
    limit: number = 10,
    sort: 'mentions' | 'sentiment' = 'mentions',
    order: 'asc' | 'desc' = 'desc'
  ): Promise<TopTickersResponse> {
    return this.statsDomain.getTopTickersFree(hours, limit, sort, order);
  }

  async getTopTickersPro(
    hours: number = 24,
    limit: number = 20,
    sort: 'mentions' | 'sentiment' = 'mentions',
    order: 'asc' | 'desc' = 'desc'
  ): Promise<TopTickersResponse> {
    return this.statsDomain.getTopTickersPro(hours, limit, sort, order);
  }

  async getTopTickers(
    hours: number = 6,
    limit: number = 10,
    sort: 'mentions' | 'sentiment' = 'mentions',
    order: 'asc' | 'desc' = 'desc',
    isPro: boolean
  ): Promise<TopTickersResponse> {
    return this.statsDomain.getTopTickers(hours, limit, sort, order, isPro);
  }

  async getAnalytics(ticker: string, hours: number, interval: number, isPro: boolean): Promise<DetailedAnalytics> {
    return this.statsDomain.getAnalytics(ticker, hours, interval, isPro);
  }

  async getTimeSeries(
    hours: number = 24,
    ticker?: string,
    subreddit?: string,
    interval: string = '15min',
    isPro: boolean = false
  ): Promise<HourlyStatsResponse> {
    return this.statsDomain.getTimeSeries(hours, ticker, subreddit, interval, isPro);
  }

  async getTickerExplain(ticker: string, limit: number = 10): Promise<TickerContribution[]> {
    return this.statsDomain.getTickerExplain(ticker, limit);
  }

  async getSpikes(ticker: string, hours: number = 12): Promise<SpikesByTickerResponse> {
    return this.statsDomain.getSpikes(ticker, hours);
  }

  async getSpikesTop(limit: number = 20): Promise<SpikesTopResponse> {
    return this.statsDomain.getSpikesTop(limit);
  }

  async getSpikesTopTickers(hours: number = 3, limit: number = 20): Promise<SpikesTopResponse> {
    return this.statsDomain.getSpikesTopTickers(hours, limit);
  }

  async getPriceLinkage(
    ticker: string,
    sessionType: PriceLinkageSessionType | string = 'REGULAR',
    windowDays: number = 60
  ): Promise<PriceLinkageResponse> {
    return this.statsDomain.getPriceLinkage(ticker, sessionType, windowDays);
  }

  async getTopPriceLinkage(
    limit: number = 20,
    sessionType: PriceLinkageSessionType | string = 'REGULAR',
    windowDays: number = 60
  ): Promise<TopPriceLinkageResponse> {
    return this.statsDomain.getTopPriceLinkage(limit, sessionType, windowDays);
  }

  async login(email: string): Promise<void> {
    return this.authDomain.login(email);
  }

  async verifyToken(token: string, email: string): Promise<VerifyResponse> {
    return this.authDomain.verifyToken(token, email);
  }

  async loginWithGoogle(idToken: string): Promise<VerifyResponse> {
    return this.authDomain.loginWithGoogle(idToken);
  }

  async refreshAccessToken(): Promise<string> {
    return this.authDomain.refreshAccessToken();
  }

  async logout(): Promise<void> {
    return this.authDomain.logout();
  }

  isAuthenticated(): boolean {
    return this.authDomain.isAuthenticated();
  }

  async getMe(): Promise<VerifyResponse> {
    return this.authDomain.getMe();
  }

  async createCheckoutSession(): Promise<StripeCheckoutResponse> {
    return this.billingDomain.createCheckoutSession();
  }

  async createPortalSession(): Promise<StripePortalResponse> {
    return this.billingDomain.createPortalSession();
  }

  async getCheckoutStatus(sessionId: string): Promise<CheckoutStatusResponse> {
    return this.billingDomain.getCheckoutStatus(sessionId);
  }

  async getBillingHistory(limit: number = 50): Promise<BillingHistoryItem[]> {
    return this.billingDomain.getBillingHistory(limit);
  }

  async getPlans(): Promise<PlansResponse> {
    return this.billingDomain.getPlans();
  }

  async getUserKarma(username: string): Promise<UserKarma> {
    return this.marketDomain.getUserKarma(username);
  }

  async analyzeSentiment(text: string): Promise<SentimentAnalysisResult> {
    return this.marketDomain.analyzeSentiment(text);
  }

  async getMetrics(): Promise<string> {
    return this.marketDomain.getMetrics();
  }

  async getStockPrice(ticker: string): Promise<{
    ticker: string;
    price: number;
    currency: string;
    timestamp: string;
    source: string;
  }> {
    return this.marketDomain.getStockPrice(ticker);
  }

  async getStockChange(ticker: string, days: number = 1): Promise<{
    ticker: string;
    current_price: number;
    previous_price: number;
    change: number;
    change_percent: number;
    period_days: number;
  }> {
    return this.marketDomain.getStockChange(ticker, days);
  }

  async getStockInfo(ticker: string): Promise<{
    ticker: string;
    company_name: string;
    sector: string;
    industry: string;
    market_cap: number;
    pe_ratio: number;
    dividend_yield: number;
  }> {
    return this.marketDomain.getStockInfo(ticker);
  }

  async getMultipleStockPrices(tickers: string[]): Promise<Array<{
    ticker: string;
    price: number;
    change: number;
    change_percent: number;
  }>> {
    return this.marketDomain.getMultipleStockPrices(tickers);
  }

  async searchContent(params: SearchContentParams): Promise<SearchContentResponse> {
    return this.marketDomain.searchContent(params);
  }

  async searchByTicker(ticker: string, limit: number = 50): Promise<SearchContentResponse> {
    return this.marketDomain.searchByTicker(ticker, limit);
  }

  async searchBySentiment(sentiment: 'positive' | 'negative' | 'neutral', limit: number = 50): Promise<SearchContentResponse> {
    return this.marketDomain.searchBySentiment(sentiment, limit);
  }

  async searchByPrediction(prediction: 'bullish' | 'bearish' | 'neutral', limit: number = 50): Promise<SearchContentResponse> {
    return this.marketDomain.searchByPrediction(prediction, limit);
  }

  async searchByAuthor(author: string, limit: number = 50): Promise<SearchContentResponse> {
    return this.marketDomain.searchByAuthor(author, limit);
  }
}

export const apiService = new ApiService();
